import { makeAutoObservable } from 'mobx';
import { OptionModel, Option } from '../../../model/professional/option-model';
import toastStore from '../../../store/toast-store';
import optionStore from '../../../store/option-store';

/**
 * OptionFormViewModel - Gère la logique métier du formulaire d'option
 * 
 * Ce ViewModel est responsable de la gestion des opérations liées au formulaire d'option,
 * notamment la création et la mise à jour des options. Il suit le pattern MVVM en séparant
 * la logique métier de l'interface utilisateur.
 */
class OptionFormViewModel {
  isLoading: boolean = false;
  error: string | null = null;
  imageFile: File | null = null;
  imagePreview: string | null = null;
  imageRemoved: boolean = false;
  private optionModel: OptionModel;
  name: string = '';
  description: string = '';
  price: number = 0;
  isActive: boolean = true;
  isSubmitting: boolean = false;

  setName(name: string): void {
    this.name = name;
  }

  setDescription(description: string): void {
    this.description = description;
  }

  setPrice(price: number): void {
    this.price = price;
  }

  setIsActive(isActive: boolean): void {
    this.isActive = isActive;
  }

  constructor() {
    makeAutoObservable(this);
    this.optionModel = OptionModel.getInstance();
    this.resetImageState();
  }

  /**
   * Gère les états de chargement de manière centralisée
   * @param isLoading État de chargement à définir
   */
  setLoading(isLoading: boolean): void {
    this.isLoading = isLoading;
  }

  /**
   * Gère les erreurs de manière centralisée
   * @param message Message d'erreur à afficher
   */
  handleError(message: string): void {
    console.error(message);
    this.setError(message);
    toastStore.error(message);
  }

  /**
   * Définit le message d'erreur
   * @param value Message d'erreur ou null
   */
  setError(value: string | null): void {
    this.error = value;
  }
  
  /**
   * Gère le changement d'image
   * @param file Fichier image sélectionné
   */
  handleImageChange(file: File): void {
    this.imageFile = file;
    this.imageRemoved = false;
    
    // Créer une prévisualisation de l'image
    const reader = new FileReader();
    reader.onloadend = () => {
      // Utiliser une action pour modifier imagePreview
      this.setImagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  }
  
  /**
   * Définit la prévisualisation de l'image (action MobX explicite)
   * @param value Valeur de la prévisualisation
   */
  setImagePreview(value: string | null): void {
    this.imagePreview = value;
  }
  
  /**
   * Supprime l'image sélectionnée ou existante
   */
  handleRemoveImage(): void {
    this.setImagePreview(null);
    this.imageFile = null;
    this.imageRemoved = true;
  }

  initializeForm(option: Option | null): void {
    if (option) {
      this.setName(option.name || '');
      this.setDescription(option.description || '');
      this.setPrice(option.price || 0);
      this.setIsActive(option.isActive ?? true);
      this.setError(null);
    } else {
      this.resetForm();
    }
  }

  resetForm(): void {
    this.isSubmitting = false;
    this.error = null;
    this.name = '';
    this.description = '';
    this.price = 0;
    this.isActive = true;
  }
  
  /**
   * Réinitialise l'état des images
   * @param existingImageUrl URL d'une image existante (pour l'édition)
   */
  resetImageState(existingImageUrl: string | null = null): void {
    this.imageFile = null;
    this.setImagePreview(existingImageUrl);
    this.imageRemoved = false;
  }
  
  /**
   * Prépare les données du formulaire avec la gestion des images
   * @param formData FormData de base
   * @param existingImage URL d'une image existante
   */
  prepareImageData(formData: FormData, existingImage?: string): void {
    if (this.imageFile) {
      formData.append('image', this.imageFile);
    }
    
    if (this.imageRemoved && existingImage) {
      formData.append('removeImage', 'true');
    }
  }

  /**
   * Valide les champs obligatoires pour une option
   * @param formData Données du formulaire
   * @returns true si tous les champs obligatoires sont remplis, false sinon
   */
  validateOptionData(formData: FormData): boolean {
    const name = formData.get('name') as string;
    const price = formData.get('price') as string;
    
    if (!name || name.trim() === '') {
      this.handleError('Le nom de l\'option est obligatoire');
      return false;
    }
    
    if (price === null || price === undefined || isNaN(parseFloat(price))) {
      this.handleError('Le prix de l\'option est obligatoire et doit être un nombre valide');
      return false;
    }
    
    // Vérifier que le prix n'est pas 0
    if (parseFloat(price) <= 0) {
      this.handleError('Le prix de l\'option doit être supérieur à 0€');
      return false;
    }
    
    return true;
  }

  /**
   * Crée une nouvelle option
   * @param formData Données du formulaire pour la création de l'option
   * @returns L'ID de l'option créée ou null en cas d'erreur
   */
  async submitCreateForm(formData: FormData): Promise<string | null> {
    // Ajouter l'image au formData si nécessaire
    this.prepareImageData(formData);
    
    // Valider les champs obligatoires
    if (!this.validateOptionData(formData)) {
      return null;
    }
    
    try {
      this.setLoading(true);
      const response = await this.optionModel.createOption(formData);
      
      if (!response?.success || !response.data) {
        throw new Error(response?.message || 'Erreur lors de la création de l\'option');
      }
      
      optionStore.addOption(response.data);
      toastStore.success(response.message || 'Option créée avec succès');
      return response.data.id;
    } catch (error: any) {
      this.handleError(error.message || 'Erreur lors de la création de l\'option');
      return null;
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Vérifie si l'option est la dernière option active dans sa catégorie
   * @param categoryId ID de la catégorie
   * @param optionId ID de l'option à exclure de la vérification
   * @returns true si c'est la dernière option active, false sinon
   */
  private isLastActiveOptionInCategory(categoryId: string, optionId: string): boolean {
    const optionStore = require('../../../store/option-store').default;
    const categoryOptions = optionStore.getOptionsByCategoryId(categoryId);
    
    // Filtrer pour obtenir uniquement les options actives autres que celle en cours de modification
    const otherActiveOptions = categoryOptions.filter(
      (option: { id: string; isActive: boolean }) => option.id !== optionId && option.isActive
    );
    
    // Si aucune autre option active n'est trouvée, c'est la dernière option active
    return otherActiveOptions.length === 0;
  }
  
  /**
   * Désactive une catégorie si nécessaire
   * @param categoryId ID de la catégorie à désactiver
   */
  private async disableCategoryIfNeeded(categoryId: string): Promise<void> {
    const categoryStore = require('../../../store/category-store').default;
    const category = categoryStore.getCategoryById(categoryId);
    
    if (category && category.isActive) {
      const categoryModel = require('../../../model/professional/option-category-model').OptionCategoryModel.getInstance();
      try {
        const response = await categoryModel.toggleCategoryStatus(categoryId, false);
        if (response.success && response.data) {
          categoryStore.updateCategoryInStore(categoryId, response.data);
          toastStore.info('La catégorie a été automatiquement désactivée car elle ne contient plus d\'options actives');
        }
      } catch (error) {
        console.error('Erreur lors de la désactivation de la catégorie:', error);
      }
    }
  }

  /**
   * Met à jour une option existante
   * @param optionId ID de l'option à mettre à jour
   * @param formData Données du formulaire pour la mise à jour de l'option
   * @param currentIsActive État actuel d'activation de l'option
   * @param newIsActive Nouvel état d'activation de l'option
   * @returns true si la mise à jour a réussi, false sinon
   */
  async submitUpdateForm(
    optionId: string,
    formData: FormData,
    currentIsActive: boolean,
    newIsActive: boolean,
    existingImage?: string
  ): Promise<boolean> {
    // Ajouter l'image au formData si nécessaire
    this.prepareImageData(formData, existingImage);
    
    // Valider les champs obligatoires
    if (!this.validateOptionData(formData)) {
      return false;
    }
    
    // Récupérer l'ID de la catégorie avant la mise à jour
    const categoryId = formData.get('categoryId') as string;
    
    // Vérifier si c'est la dernière option active et si on la désactive
    const isLastActive = currentIsActive && !newIsActive && 
                        this.isLastActiveOptionInCategory(categoryId, optionId);
    
    try {
      this.setLoading(true);
      const updateResponse = await this.optionModel.updateOption(optionId, formData);
      
      if (!updateResponse?.success) {
        throw new Error(updateResponse?.message || 'Erreur lors de la mise à jour de l\'option');
      }
      
      optionStore.updateOptionInStore(optionId, updateResponse.data || {});
      
      // Si le statut a changé, mettre à jour le statut
      if (currentIsActive !== newIsActive) {
        const statusResponse = await this.optionModel.toggleOptionStatus(optionId, newIsActive);
        
        if (!statusResponse?.success) {
          throw new Error(statusResponse?.message || 'Erreur lors de la mise à jour du statut');
        }
        
        optionStore.updateOptionInStore(optionId, statusResponse.data || {});
        
        // Si c'était la dernière option active et qu'on la désactive, désactiver aussi la catégorie
        if (isLastActive) {
          await this.disableCategoryIfNeeded(categoryId);
        }
      }
      
      toastStore.success('Option mise à jour avec succès');
      return true;
    } catch (error: any) {
      this.handleError(error.message || 'Erreur lors de la mise à jour de l\'option');
      return false;
    } finally {
      this.setLoading(false);
    }
  }
}

const optionFormViewModel = new OptionFormViewModel();
export default optionFormViewModel;
